class CartValidator {
  constructor() {
    // Detect language from the HTML tag (works for /en/, /es/, /uk/, /fr/ subfolders)
    const lang = document.documentElement.lang ? document.documentElement.lang.substring(0, 2) : 'de';
    
    // PLACEHOLDER_VALUES: All "Please Select" strings across supported languages.
    this.invalidValues = [
      'Bitte auswählen', 
      'Bitte wählen Sie aus',
      'Please select',
      'Por favor seleccione',
      'Por favor, selecciona',
      'Por favor selecciona',
      'Elige una opción',
      'Elige una opcion',
      'Veuillez sélectionner',
      'Будь ласка, оберіть',
      'Виберіть'
    ];

    // DELIVERY CATEGORIES — used to normalize values across languages.
    // So "PDF & Original per Post" (DE) and "PDF y original por correo postal" (ES)
    // both map to 'postal' — preventing false-positive mixed-shipping errors
    // when a user switches language mid-purchase.
    this.deliveryCategories = {
      digital: [
        'Nur PDF (digital)',            // DE
        'Only PDF (digital)',           // EN
        'Solo PDF (digital)',           // ES
        'Лише PDF (цифрова версія)',    // UK
        'PDF uniquement (numérique)'    // FR (future)
      ],
      postal: [
        'PDF & Original per Post',          // DE
        'PDF & original by post',           // EN
        'PDF y original por correo postal', // ES
        'PDF та оригінал поштою',           // UK
        'PDF & original par courrier'       // FR (future)
      ]
    };

    // Define error messages based on language
    if (lang === 'en') {
      this.errorMessages = {
        mixedShipping: 'You cannot combine products with different delivery options. Please select either "Only PDF (digital)" or "PDF & original by post" for all documents.',
        required: 'Please select a delivery option.'
      };
    } else if (lang === 'es') {
      this.errorMessages = {
        mixedShipping: 'No puedes combinar productos con diferentes opciones de entrega. Por favor, selecciona "Solo PDF (digital)" o "PDF y original por correo postal" para todos los documentos.',
        required: 'Por favor, selecciona una opción de entrega.'
      };
    } else if (lang === 'uk') {
      this.errorMessages = {
        mixedShipping: 'Ви не можете поєднувати товари з різними варіантами доставки. Будь ласка, виберіть "Лише PDF (цифрова версія)" або "PDF та оригінал поштою" для всіх документів.',
        required: 'Будь ласка, оберіть варіант доставки.'
      };
    } else if (lang === 'fr') {
      this.errorMessages = {
        mixedShipping: 'Vous ne pouvez pas combiner des produits avec différentes options de livraison. Veuillez sélectionner soit "PDF uniquement (numérique)" soit "PDF & original par courrier" pour tous les documents.',
        required: 'Veuillez sélectionner une option de livraison.'
      };
    } else {
      this.errorMessages = {
        mixedShipping: 'Du kannst keine Produkte mit verschiedenen Lieferoptionen kombinieren. Bitte wähle für alle Dokumente entweder "Nur PDF (digital)" oder "PDF & Original per Post".',
        required: 'Bitte wähle eine Lieferoption aus.'
      };
    }

    this.hasError = false;
  }

  /**
   * Check if a value is a placeholder (i.e. a "please select" string in any language).
   */
  isPlaceholderValue(value) {
    if (!value || typeof value !== 'string') return true;
    return this.invalidValues.includes(value.trim());
  }

  /**
   * Get the delivery category ('digital' or 'postal') for a given value.
   * Normalizes the value across all supported languages so that semantically-equivalent
   * options match regardless of which language they were selected in.
   */
  getDeliveryCategory(value) {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (this.deliveryCategories.digital.includes(trimmed)) return 'digital';
    if (this.deliveryCategories.postal.includes(trimmed)) return 'postal';
    return null;
  }

  /**
   * Get the delivery option from a cart item using multi-level fallback.
   */
  getItemDelivery(item) {
    if (!item) return null;
    
    if (item.properties?.Lieferoption) {
      return item.properties.Lieferoption;
    }
    if (item.properties?.Versand) {
      return item.properties.Versand;
    }
    if (item.options_with_values && Array.isArray(item.options_with_values)) {
      const lastOption = item.options_with_values[item.options_with_values.length - 1];
      if (lastOption?.value) {
        return lastOption.value;
      }
    }
    if (item.variant_title) {
      const parts = item.variant_title.split(' / ');
      if (parts.length > 0) {
        return parts[parts.length - 1];
      }
    }
    return null;
  }

  toggleErrorState(hasError, message = '') {
    const shippingSelect = document.querySelector('.label-3');
    const shippingLabel = shippingSelect?.closest('.product-form__input')?.querySelector('.form__label');

    if (shippingLabel) {
      shippingLabel.style.color = hasError ? '#C44E47' : '';
    }
    if (shippingSelect) {
      shippingSelect.style.backgroundColor = hasError ? '#ffaca8' : '';
    }

    const shippingContainer = shippingSelect ? shippingSelect.closest('.select') : null;
    if (hasError && shippingContainer) {
      shippingContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      let errorEl = document.getElementById('shipping-error-message');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.id = 'shipping-error-message';
        errorEl.style.color = '#C44E47';
        errorEl.style.fontSize = '0.8em';
        errorEl.style.marginTop = '0.5em';
        errorEl.style.lineHeight = '1';
        shippingContainer.parentNode.insertBefore(errorEl, shippingContainer.nextSibling);
      }
      errorEl.innerHTML = message;
    } else {
      const existingErrorEl = document.getElementById('shipping-error-message');
      if (existingErrorEl) {
        existingErrorEl.remove();
      }
    }

    this.hasError = hasError;
  }

  clearErrorState() {
    this.toggleErrorState(false);
  }

  getCurrentlySelectedShippingOption() {
    const shippingSelect = document.querySelector('.label-3');
    if (!shippingSelect) return null;
    
    const selectedOption = shippingSelect.value;
    
    if (!selectedOption || this.isPlaceholderValue(selectedOption)) {
      return null;
    }
    
    return selectedOption;
  }

  /**
   * Validates the shipping option before adding a new item.
   * Compares DELIVERY CATEGORIES (not raw strings) to be cross-language-tolerant.
   */
  async validateBeforeAdd(cartItems, newItem) {
    console.log('[CartValidator] Incoming validation request');
    
    try {
      const newDeliveryOption = this.getCurrentlySelectedShippingOption();
      console.log('[CartValidator] Selected delivery option:', newDeliveryOption);
      
      // (1) Must choose a shipping option
      if (!newDeliveryOption) {
        console.log('[CartValidator] No valid shipping option => blocking add');
        this.toggleErrorState(true, this.errorMessages.required);
        throw new Error(this.errorMessages.required);
      }

      // (2) Check for mixed shipping — compare CATEGORIES, not raw strings
      const newCategory = this.getDeliveryCategory(newDeliveryOption);
      console.log('[CartValidator] New category:', newCategory);

      if (cartItems && cartItems.length > 0) {
        for (const item of cartItems) {
          const itemDeliveryOption = this.getItemDelivery(item);
          
          if (!itemDeliveryOption) continue;
          if (this.isPlaceholderValue(itemDeliveryOption)) continue;
          
          const itemCategory = this.getDeliveryCategory(itemDeliveryOption);
          
          // Prefer category-based comparison (cross-language-tolerant)
          // Fall back to raw string comparison if either category is unknown
          let isMismatch;
          if (itemCategory && newCategory) {
            isMismatch = itemCategory !== newCategory;
          } else {
            isMismatch = itemDeliveryOption !== newDeliveryOption;
            if (isMismatch) {
              console.warn('[CartValidator] Could not categorize one or both options — falling back to string compare');
            }
          }
          
          if (isMismatch) {
            console.log('[CartValidator] Delivery option mismatch — blocking add');
            console.log('[CartValidator]   Existing:', itemDeliveryOption, '(category:', itemCategory, ')');
            console.log('[CartValidator]   New:', newDeliveryOption, '(category:', newCategory, ')');
            this.toggleErrorState(true, this.errorMessages.mixedShipping);
            throw new Error(this.errorMessages.mixedShipping);
          }
        }
      }

      console.log('[CartValidator] Validation passed. Proceeding with add.');
      this.clearErrorState();
      return true;

    } catch (error) {
      this.toggleErrorState(true, error.message);
      throw error;
    }
  }
}

// Create a global instance so product-form.js (and the bridge) can call it
window.cartValidator = new CartValidator();

// Expose isPlaceholderValue as a free-standing function on window
window.isPlaceholderValue = function(value) {
  return window.cartValidator.isPlaceholderValue(value);
};

// Expose getItemDelivery on window for use by cart-validator-bridge.js
window.getItemDelivery = function(item) {
  return window.cartValidator.getItemDelivery(item);
};

// Expose getDeliveryCategory on window for use by cart-validator-bridge.js
window.getDeliveryCategory = function(value) {
  return window.cartValidator.getDeliveryCategory(value);
};

// Event listener: clear error state when the shipping option changes to a valid value
document.addEventListener('DOMContentLoaded', function() {
  const shippingSelect = document.querySelector('.label-3');
  if (shippingSelect) {
    shippingSelect.addEventListener('change', function() {
      if (window.cartValidator && this.value && !window.cartValidator.isPlaceholderValue(this.value)) {
        window.cartValidator.clearErrorState();
      }
    });
  }
});